/**
 * Catalogue › Articles — the screen a salesperson keeps open on the phone.
 *
 * "I look for an article, I see the stock, I see the price": a search box
 * that answers on reference, designation and brand; the catalogue tree on the
 * side (Activité → Famille → Sous-famille → Segment, then the brand), which
 * narrows the list; and one dense row per article with the counter price,
 * what is available in the chosen scope, and who sells it.
 *
 * **The scope is the session's, not this screen's.** Company, city, point of
 * sale and depot come from the back office's context (`useScope`), so the
 * company tabs here and the context panel in the header are the same switch:
 * change one and the other follows.
 *
 * **Three "no"s, never merged.** An article the chosen company does not sell
 * says *Hors périmètre* — and nothing else, since its price and stock are
 * another company's business. In the perimeter, a price that resolves to
 * nothing says *Tarif absent*, and a stock nobody in scope has recorded says
 * *Stock inconnu*, which is not *Stock à zéro*.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { EM_DASH } from "@hardauto/core-i18n";
import {
  Button,
  Checkbox,
  DataTable,
  Input,
  MultiSelect,
  Page,
  Pagination,
  Select,
  Toolbar,
  type Column,
} from "@hardauto/core-ui";

import {
  CatalogApi,
  type ConsultationArticle,
  type ConsultationRow,
  type PerimeterFilter,
} from "../api";
import { NAMESPACE } from "../i18n";
import { CATALOG_PATHS } from "../paths";
import {
  EMPTY_STRUCTURE,
  citiesOf,
  narrowStructureBy,
  pruneSelection,
  selectionOf,
  type ScopeBinding,
  type ScopeSelection,
  type StockStructure,
} from "../scope";
import { CategoryPicker } from "./consultation/CategoryPicker";
import { PriceCell, StockCell } from "./consultation/cells";
import { PriceDialog } from "./consultation/PriceDialog";
import { BrandFilterMenu, QuantityFilterMenu, TextFilterMenu } from "./consultation/ColumnMenus";
import { StockDialog } from "./consultation/StockDialog";
import "./catalog.css";

const PAGE = 50;

/** How long a prefetched article is served again. Stock moves, so not for long. */
const ARTICLE_CACHE_MS = 30_000;

export interface ConsultationScreenProps {
  client: ApiClient;
  scope: ScopeBinding;
  loadStructure?: (() => Promise<StockStructure>) | undefined;
  /** What the search box starts with — the home page's quick search. */
  initialSearch?: string | undefined;
  /** Kept for the application's wiring; prices are tariff prices, VAT included, shown as they are. */
  loadTaxRate?: (() => Promise<string>) | undefined;
}

export function ConsultationScreen({
  client,
  scope: binding,
  loadStructure,
  initialSearch,
}: ConsultationScreenProps) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new CatalogApi(client), [client]);
  const { scope } = binding;

  const [search, setSearch] = useState(initialSearch ?? "");
  const [categoryPath, setCategoryPath] = useState("");
  // The filters of this list. Each level holds several values, which are
  // alternatives. They start from the header's working context (below) and
  // are this screen's own from then on.
  const [selection, setSelection] = useState<ScopeSelection>(() => selectionOf(scope));
  const [brandsChosen, setBrandsChosen] = useState<string[]>([]);
  // Column filters, as in a spreadsheet: each matches inside its own field only.
  const [fields, setFields] = useState({
    reference: "",
    designation: "",
    referenceBase: "",
    ipc: "",
  });
  // Quantity column filter: at least this much on hand.
  const [minQuantity, setMinQuantity] = useState("");
  const minQuantityTerm = useDebounced(minQuantity, 250);
  // The category tree takes a third of the width; hiding it gives the list the
  // whole page. Remembered per browser, never required.
  const [showTree, setShowTree] = useState(readTreePreference);
  const fieldTerms = useDebounced(fields, 250);
  const [perimeter, setPerimeter] = useState<PerimeterFilter>("in");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [stockRow, setStockRow] = useState<ConsultationRow | null>(null);
  const [priceRow, setPriceRow] = useState<ConsultationRow | null>(null);
  const term = useDebounced(search, 250);

  const structureFetcher = useCallback(
    () => (loadStructure ? loadStructure() : Promise.resolve(EMPTY_STRUCTURE)),
    [loadStructure],
  );
  const structure = useResource(structureFetcher);
  const known = structure.data ?? EMPTY_STRUCTURE;
  const narrowed = narrowStructureBy(known, selection);

  const treeFetcher = useCallback(async () => {
    const [tree, levels] = await Promise.all([api.categoryTree(false), api.levels()]);
    return { roots: tree.roots, levels: levels.items };
  }, [api]);
  const tree = useResource(treeFetcher);

  const brandsFetcher = useCallback(() => api.consultationBrands(), [api]);
  const brands = useResource(brandsFetcher);

  const fetcher = useCallback(
    () =>
      api.consultation({
        search: term || undefined,
        reference: fieldTerms.reference || undefined,
        designation: fieldTerms.designation || undefined,
        referenceBase: fieldTerms.referenceBase || undefined,
        ipc: fieldTerms.ipc || undefined,
        categoryPath: categoryPath || undefined,
        brands: brandsChosen,
        companyCodes: selection.companyCodes,
        cities: selection.cities,
        pointOfSaleCodes: selection.pointOfSaleCodes,
        siteCodes: selection.siteCodes,
        perimeter: selection.companyCodes.length > 0 ? perimeter : undefined,
        availableOnly,
        minAvailable: minQuantityTerm,
        active: scope.active,
        afterId: cursor,
        limit: PAGE,
      }),
    [
      api,
      term,
      fieldTerms,
      categoryPath,
      brandsChosen,
      selection,
      perimeter,
      availableOnly,
      minQuantityTerm,
      scope.active,
      cursor,
    ],
  );
  const rows = useResource(fetcher);

  // The stock of an article is fetched as the pointer arrives on its quantity, and
  // kept for a moment, so the dialog the click opens has its data already. Most of
  // a request's time is the request itself, not what it reads: starting earlier is
  // what makes the click feel instant.
  const cache = useRef(new Map<string, { at: number; read: Promise<ConsultationArticle> }>());
  const loadArticle = useCallback(
    (id: string): Promise<ConsultationArticle> => {
      const kept = cache.current.get(id);
      if (kept && Date.now() - kept.at < ARTICLE_CACHE_MS) return kept.read;
      const read = api.consultationArticle(id);
      cache.current.set(id, { at: Date.now(), read });
      // A failed read must not be served again.
      read.catch(() => cache.current.delete(id));
      return read;
    },
    [api],
  );
  const prefetch = (id: string) => {
    void loadArticle(id).catch(() => undefined);
  };

  const restart = () => {
    setCursor(undefined);
  };
  /** Change some levels of the selection, clearing the narrower choices that no longer fit. */
  const choose = (patch: Partial<ScopeSelection>) => {
    setSelection((current) => pruneSelection(known, { ...current, ...patch }));
    restart();
  };

  // The header's working context is where the filters *start*: a change made
  // there restarts them from it. The other way round is not done — several
  // chosen values have no single place in the header — so the list keeps its
  // own selection afterwards.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSelection(selectionOf(scope));
    setCursor(undefined);
    // `scope` is an object the application rebuilds; its four fields are the value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyCode, scope.city, scope.pointOfSaleCode, scope.siteCode]);

  const tariffLabel = rows.data?.tariff?.label ?? t("consultation.basePrice");
  // The wording every multi-value filter of the toolbar shares.
  const multi = {
    countLabel: (count: number) => t("consultation.selectedCount", { count }),
    clearLabel: t("consultation.clearFilter"),
    searchLabel: t("consultation.searchOptions"),
  };

  // The desk's six columns, in the order a counter reads a line: which
  // article, what it is, whose make, what it costs the customer, how many
  // there are, who sells it. Segment, physical and reserved stock are the
  // fiche's — one click away, and off a list read at a glance.
  const columns: Column<ConsultationRow>[] = [
    {
      key: "article",
      header: t("consultation.column.article"),
      menu: (
        <TextFilterMenu
          column={t("consultation.column.article")}
          value={fields.reference}
          onChange={(value) => {
            setFields({ ...fields, reference: value });
            restart();
          }}
        />
      ),
      render: (row) => (
        <Link
          to={CATALOG_PATHS.consultationArticle}
          params={{ id: row.id }}
          className="hardauto-consultation__ref"
        >
          <code>{row.reference}</code>
        </Link>
      ),
    },
    {
      key: "designation",
      header: t("consultation.column.designation"),
      menu: (
        <TextFilterMenu
          column={t("consultation.column.designation")}
          value={fields.designation}
          onChange={(value) => {
            setFields({ ...fields, designation: value });
            restart();
          }}
        />
      ),
      render: (row) => (
        <span className="hardauto-consultation__designation">{row.designation}</span>
      ),
    },
    {
      key: "brand",
      header: t("consultation.column.brand"),
      menu: (
        <BrandFilterMenu
          column={t("consultation.column.brand")}
          options={brands.data?.items ?? []}
          selected={brandsChosen}
          onChange={(next) => {
            setBrandsChosen(next);
            restart();
          }}
        />
      ),
      render: (row) =>
        row.brand ? row.brand : <span className="hardauto-cell__muted">{EM_DASH}</span>,
    },
    {
      key: "referenceBase",
      header: t("consultation.column.referenceBase"),
      menu: (
        <TextFilterMenu
          column={t("consultation.column.referenceBase")}
          value={fields.referenceBase}
          onChange={(value) => {
            setFields({ ...fields, referenceBase: value });
            restart();
          }}
        />
      ),
      render: (row) =>
        row.reference_base ? (
          row.reference_base
        ) : (
          <span className="hardauto-cell__muted">{EM_DASH}</span>
        ),
    },
    {
      key: "ipc",
      header: t("consultation.column.ipc"),
      menu: (
        <TextFilterMenu
          column={t("consultation.column.ipc")}
          value={fields.ipc}
          onChange={(value) => {
            setFields({ ...fields, ipc: value });
            restart();
          }}
        />
      ),
      render: (row) =>
        row.ipc ? row.ipc : <span className="hardauto-cell__muted">{EM_DASH}</span>,
    },
    {
      key: "price",
      // The tariffs are loaded as VAT-inclusive prices, so they are shown as they are.
      header: t("consultation.column.priceTtc"),
      align: "end",
      render: (row) => (
        <PriceCell
          row={row}
          locale={locale}
          onOpen={() => setPriceRow(row)}
          onPrefetch={() => prefetch(row.id)}
        />
      ),
    },
    {
      key: "quantity",
      header: t("consultation.column.quantity"),
      menu: (
        <QuantityFilterMenu
          column={t("consultation.column.quantity")}
          value={minQuantity}
          onChange={(value) => {
            setMinQuantity(value);
            restart();
          }}
        />
      ),
      align: "end",
      render: (row) => (
        <StockCell
          row={row}
          locale={locale}
          onOpen={() => setStockRow(row)}
          onPrefetch={() => prefetch(row.id)}
        />
      ),
    },
  ];

  return (
    <Page
      title={t("consultation.title")}
      description={t("consultation.description")}
      eyebrow={rows.data ? t("consultation.count", { count: rows.data.total }) : undefined}
    >
      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("consultation.searchLabel")}
          placeholder={t("consultation.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            restart();
          }}
        />
        <MultiSelect
          label={t("consultation.city")}
          allLabel={t("consultation.anyCity")}
          options={citiesOf(known).map((city) => ({ value: city, label: city }))}
          selected={selection.cities}
          onChange={(next) => choose({ cities: next })}
          {...multi}
        />
        <MultiSelect
          label={t("consultation.pointOfSale")}
          allLabel={t("consultation.anyPointOfSale")}
          options={narrowed.pointsOfSale.map((point) => ({ value: point.code, label: point.name }))}
          selected={selection.pointOfSaleCodes}
          onChange={(next) => choose({ pointOfSaleCodes: next })}
          {...multi}
        />
        <MultiSelect
          label={t("consultation.depot")}
          allLabel={t("consultation.anyDepot")}
          options={narrowed.sites.map((site) => ({ value: site.code, label: site.name }))}
          selected={selection.siteCodes}
          onChange={(next) => choose({ siteCodes: next })}
          {...multi}
        />
        <MultiSelect
          label={t("consultation.brand")}
          allLabel={t("consultation.anyBrand")}
          options={(brands.data?.items ?? []).map((name) => ({ value: name, label: name }))}
          selected={brandsChosen}
          onChange={(next) => {
            setBrandsChosen(next);
            restart();
          }}
          {...multi}
        />
        {selection.companyCodes.length > 0 ? (
          <Field label={t("consultation.perimeter")}>
            <Select
              value={perimeter}
              onChange={(event) => {
                setPerimeter(event.target.value as PerimeterFilter);
                restart();
              }}
            >
              <option value="in">{t("consultation.perimeterIn")}</option>
              <option value="out">{t("consultation.perimeterOut")}</option>
              <option value="all">{t("consultation.perimeterAll")}</option>
            </Select>
          </Field>
        ) : null}
        <Checkbox
          label={t("consultation.availableOnly")}
          checked={availableOnly}
          onChange={(event) => {
            setAvailableOnly(event.target.checked);
            restart();
          }}
        />
        <Button
          variant="secondary"
          aria-expanded={showTree}
          onClick={() =>
            setShowTree((open) => {
              writeTreePreference(!open);
              return !open;
            })
          }
        >
          {showTree ? t("consultation.hideTree") : t("consultation.showTree")}
        </Button>
      </Toolbar>

      <div
        className={
          showTree ? "hardauto-consultation" : "hardauto-consultation hardauto-consultation--full"
        }
      >
        {showTree ? (
          <CategoryPicker
            roots={tree.data?.roots ?? []}
            levels={tree.data?.levels ?? []}
            selected={categoryPath}
            onSelect={(path) => {
              setCategoryPath(path);
              restart();
            }}
          />
        ) : null}

        <div className="hardauto-consultation__results">
          <DataTable<ConsultationRow>
            // The tariff the prices are read from, said once over the column
            // rather than on every row.
            caption={`${t("consultation.caption")} · ${tariffLabel}`}
            density="compact"
            columns={columns}
            rows={rows.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={rows.loading}
            loadingLabel={t("consultation.loading")}
            error={
              rows.failed
                ? { title: t("error.title"), onRetry: rows.reload, retryLabel: t("error.retry") }
                : undefined
            }
            empty={{
              title: t("consultation.empty.title"),
              description: t("consultation.empty.body"),
            }}
          />

          <Pagination
            nextLabel={t("consultation.next")}
            previousLabel={t("consultation.previous")}
            onNext={
              rows.data?.next_cursor
                ? () => setCursor(rows.data?.next_cursor ?? undefined)
                : undefined
            }
            onPrevious={cursor ? () => setCursor(undefined) : undefined}
            {...(rows.data ? { summary: t("consultation.count", { count: rows.data.total }) } : {})}
            busy={rows.refreshing}
          />
        </div>
      </div>

      {priceRow ? (
        <PriceDialog load={loadArticle} row={priceRow} onClose={() => setPriceRow(null)} />
      ) : null}
      {stockRow ? (
        <StockDialog
          load={loadArticle}
          row={stockRow}
          scope={selection}
          onClose={() => setStockRow(null)}
        />
      ) : null}
    </Page>
  );
}

const TREE_KEY = "hardauto.consultation.tree";

function readTreePreference(): boolean {
  try {
    return window.localStorage.getItem(TREE_KEY) !== "hidden";
  } catch {
    return true;
  }
}

function writeTreePreference(visible: boolean): void {
  try {
    window.localStorage.setItem(TREE_KEY, visible ? "shown" : "hidden");
  } catch {
    // Private window or blocked storage: the choice just lasts for this visit.
  }
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="hardauto-toolbar__field">
      <span className="hardauto-visually-hidden">{label}</span>
      {children}
    </label>
  );
}
